import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import PageLoader from '../components/PageLoader';
import HELP_ARTICLES, { TIER_LABELS, TIER_COLORS } from '../data/helpArticles';
import { parseFrontmatter, stripLeadingTitle } from '../utils/parseFrontmatter';

export default function HelpArticle() {
  const { slug } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);

  useEffect(() => {
    if (!zoomedImage) return;
    const onKeyDown = e => { if (e.key === 'Escape') setZoomedImage(null); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [zoomedImage]);

  const index = HELP_ARTICLES.findIndex(a => a.slug === slug);
  const entry = HELP_ARTICLES[index];
  const prevEntry = HELP_ARTICLES[index - 1];
  const nextEntry = HELP_ARTICLES[index + 1];

  useEffect(() => {
    if (!entry) { setNotFound(true); return; }
    setArticle(null);
    setNotFound(false);
    fetch(`/help/${entry.file}`)
      .then(r => r.text())
      .then(raw => {
        const { data, content } = parseFrontmatter(raw);
        setArticle({ data, content: stripLeadingTitle(content) });
      })
      .catch(() => setNotFound(true));
  }, [entry]);

  if (notFound) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '2rem 1rem', textAlign: 'center', color: theme.textSecondary }}>
        <p>That guide couldn't be found.</p>
        <Link to="/dashboard/help" style={{ color: theme.primary }}>Back to all guides</Link>
      </div>
    );
  }
  if (!article) return <PageLoader />;

  const { data, content } = article;
  const tierColor = TIER_COLORS[data.tier] || TIER_COLORS.starter;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '1rem 1.1rem 3rem' }}>
      <button
        onClick={() => navigate('/dashboard/help')}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.25rem',
          background: 'none', border: 'none', cursor: 'pointer',
          color: theme.primary, fontSize: '0.9rem', fontWeight: 600,
          padding: '0.4rem 0.2rem', marginBottom: '0.75rem', WebkitTapHighlightColor: 'transparent',
        }}
      >
        <ChevronLeft size={18} /> All guides
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0, lineHeight: 1.25 }}>
          {data.title}
        </h1>
      </div>
      <span style={{
        display: 'inline-block',
        fontSize: '0.7rem',
        fontWeight: 600,
        padding: '0.15rem 0.55rem',
        borderRadius: 10,
        background: `${tierColor}22`,
        color: tierColor,
        marginBottom: '1.25rem',
      }}>
        {TIER_LABELS[data.tier] || data.tier} feature
      </span>

      <ArticleBody content={content} theme={theme} navigate={navigate} onZoom={setZoomedImage} />

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '0.75rem',
        marginTop: '2.5rem',
        paddingTop: '1.25rem',
        borderTop: `1px solid ${theme.border}`,
      }}>
        {prevEntry ? (
          <button onClick={() => navigate(`/dashboard/help/${prevEntry.slug}`)} style={navButtonStyle(theme, 'left')}>
            <ChevronLeft size={16} /> Previous
          </button>
        ) : <span />}
        {nextEntry ? (
          <button onClick={() => navigate(`/dashboard/help/${nextEntry.slug}`)} style={navButtonStyle(theme, 'right')}>
            Next <ChevronRight size={16} />
          </button>
        ) : <span />}
      </div>

      {zoomedImage && (
        <div
          onClick={() => setZoomedImage(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.8)', padding: '1rem',
          }}
        >
          <button
            type="button"
            onClick={() => setZoomedImage(null)}
            aria-label="Close"
            style={{
              position: 'absolute', top: '1rem', right: '1rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', cursor: 'pointer',
            }}
          >
            <X size={20} />
          </button>
          <img
            src={zoomedImage.src}
            alt={zoomedImage.alt}
            style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 10, objectFit: 'contain', cursor: 'zoom-out' }}
          />
        </div>
      )}
    </div>
  );
}

function navButtonStyle(theme, align) {
  return {
    display: 'flex', alignItems: 'center', gap: '0.3rem',
    background: theme.surfaceAlt, border: `0.5px solid ${theme.border}`, borderRadius: 8,
    padding: '0.6rem 0.9rem', cursor: 'pointer', color: theme.text, fontSize: '0.85rem', fontWeight: 600,
    marginLeft: align === 'right' ? 'auto' : 0, WebkitTapHighlightColor: 'transparent',
  };
}

// Known internal help slugs, so links like `./song-library` can route
// in-app via react-router instead of a full page reload.
const KNOWN_SLUGS = new Set(HELP_ARTICLES.map(a => a.slug));

function ArticleBody({ content, theme, navigate, onZoom }) {
  return (
    <div style={{ fontSize: '1rem', lineHeight: 1.65, color: theme.text }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h2: ({ children }) => (
            <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: theme.text, margin: '1.75rem 0 0.6rem' }}>{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: theme.text, margin: '1.4rem 0 0.5rem' }}>{children}</h3>
          ),
          p: ({ children }) => (
            <p style={{ margin: '0 0 0.9rem' }}>{children}</p>
          ),
          ul: ({ children }) => (
            <ul style={{ margin: '0 0 0.9rem', paddingLeft: '1.3rem' }}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol style={{ margin: '0 0 0.9rem', paddingLeft: '1.3rem' }}>{children}</ol>
          ),
          li: ({ children }) => (
            <li style={{ margin: '0 0 0.4rem' }}>{children}</li>
          ),
          blockquote: ({ children }) => (
            <div style={{
              background: theme.surfaceAlt,
              borderLeft: `3px solid ${theme.primary}`,
              borderRadius: '0 8px 8px 0',
              padding: '0.75rem 1rem',
              margin: '0 0 1rem',
              fontSize: '0.92rem',
              color: theme.textSecondary,
            }}>
              {children}
            </div>
          ),
          img: ({ src, alt }) => {
            const resolved = src.startsWith('./img/') ? `/help/img/${src.slice('./img/'.length)}` : src;
            return (
              <button
                type="button"
                onClick={() => onZoom({ src: resolved, alt })}
                aria-label="Zoom in on screenshot"
                style={{
                  position: 'relative', display: 'block', width: '100%',
                  margin: '0.5rem 0 1.25rem', padding: 0, border: 'none', background: 'none', cursor: 'zoom-in',
                }}
              >
                <img
                  src={resolved}
                  alt={alt}
                  style={{ maxWidth: '100%', height: 'auto', borderRadius: 10, display: 'block', border: `1px solid ${theme.border}` }}
                />
                <span style={{
                  position: 'absolute', top: '0.5rem', right: '0.5rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.5)', color: '#fff',
                }}>
                  <ZoomIn size={15} />
                </span>
              </button>
            );
          },
          table: ({ children }) => (
            <div style={{ overflowX: 'auto', margin: '0 0 1rem', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.88rem' }}>{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th style={{ textAlign: 'left', padding: '0.5rem 0.7rem', borderBottom: `2px solid ${theme.border}`, color: theme.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>{children}</th>
          ),
          td: ({ children }) => (
            <td style={{ padding: '0.5rem 0.7rem', borderBottom: `1px solid ${theme.border}`, verticalAlign: 'top' }}>{children}</td>
          ),
          code: ({ children }) => (
            <code style={{ background: theme.surfaceAlt, borderRadius: 4, padding: '0.1rem 0.35rem', fontSize: '0.88em', fontFamily: 'monospace' }}>{children}</code>
          ),
          a: ({ href, children }) => {
            const cleanHref = (href || '').replace(/^\.\//, '');
            if (KNOWN_SLUGS.has(cleanHref)) {
              return (
                <a
                  href={`/dashboard/help/${cleanHref}`}
                  onClick={e => { e.preventDefault(); navigate(`/dashboard/help/${cleanHref}`); }}
                  style={{ color: theme.primary, fontWeight: 600 }}
                >
                  {children}
                </a>
              );
            }
            const isInternal = (href || '').startsWith('/');
            return (
              <a
                href={href}
                target={isInternal ? undefined : '_blank'}
                rel={isInternal ? undefined : 'noopener noreferrer'}
                style={{ color: theme.primary, fontWeight: 600 }}
              >
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
